import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/useAuthStore';
import { Button } from '@/components/ui/Button';
import { safeRedirect, isTeam, oauthErrorInUrl, takeLoginRefusal } from './loginFlow';
import { apiRequest } from '@/lib/apiBase';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectTo = safeRedirect(searchParams.get('redirect'));
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const signOut = useAuthStore((s) => s.signOut);

  // Back from Google? Only the message depends on it — who gets in is the
  // role the server reads from the database.
  const viaGoogle = useRef(searchParams.get('via') === 'google');
  const [noAccess, setNoAccess] = useState(() => {
    const refused = viaGoogle.current ? oauthErrorInUrl(window.location) : null;
    return !!refused?.notAllowed;
  });
  const [googleFailed, setGoogleFailed] = useState(() => {
    const refused = viaGoogle.current ? oauthErrorInUrl(window.location) : null;
    return !!refused && !refused.notAllowed;
  });
  // Why the server just signed this person out (not on the list, password
  // login off, logged out elsewhere, 30 days) — worker lib/authGate.ts.
  const [refusal] = useState<string | null>(() => takeLoginRefusal());

  // The password form is the one-week backup (owner, 2026-10-21): shown only
  // while its switch is on. Google is the way in.
  const [passwordLogin, setPasswordLogin] = useState(false);
  useEffect(() => {
    let alive = true;
    apiRequest('/auth/login-options', {})
      .then((r) => (r.ok ? r.json() : null))
      .then((o: { password?: boolean } | null) => { if (alive) setPasswordLogin(o?.password === true); })
      .catch(() => { /* Google stays */ });
    return () => { alive = false; };
  }, []);

  // A session that is — or becomes — known while this page is open takes the
  // person on. The start-up refresh can be slower than the app waits (a phone
  // waking up on a weak connection): the app showed this page, the session
  // landed seconds later, and the admin sat on a login form while the app was
  // in fact signed in — notifications included (owner, 2026-10-01).
  useEffect(() => {
    if (!user || !profile) return;
    if (viaGoogle.current && !isTeam(profile.role)) {
      // ⚠️ ANY Gmail account can finish a Google sign-in, and Supabase then
      // creates a user for it. Only an existing admin or staff profile gets in;
      // everyone else is told so and signed straight back out.
      viaGoogle.current = false;
      setNoAccess(true);
      void signOut();
      return;
    }
    navigate(redirectTo ?? '/', { replace: true });
  }, [user, profile, redirectTo, navigate, signOut]);

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    viaGoogle.current = false;
    setNoAccess(false);
    setGoogleFailed(false);
    setLoading(true);
    setError(null);

    const { error: err } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (err) {
      setError('Email ou mot de passe incorrect.');
    } else {
      navigate(redirectTo ?? '/');
    }
    setLoading(false);
  }

  async function handleGoogleLogin() {
    setGoogleLoading(true);
    setError(null);
    setNoAccess(false);
    setGoogleFailed(false);
    // Back to THIS page, which decides: the team goes on, anyone else is refused.
    const back = new URLSearchParams();
    if (redirectTo) back.set('redirect', redirectTo);
    back.set('via', 'google');
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/login?${back.toString()}` },
    });
    if (err) {
      setGoogleFailed(true);
      setGoogleLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/storiz-logo.svg" alt="Storiz" className="h-10 mx-auto mb-3" onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }} />
          <h1 className="text-2xl font-bold text-gray-900">Connexion</h1>
          <p className="text-gray-500 text-sm mt-1">Accédez à votre espace album</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {noAccess && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
              <p className="font-semibold">Ce compte Google n’a pas accès.</p>
              <p className="mt-0.5">Seuls les emails autorisés par Storiz peuvent se connecter. Aucun compte n’a été créé.</p>
            </div>
          )}
          {googleFailed && (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
              La connexion Google n’a pas abouti. Réessayez.
            </div>
          )}
          {refusal && !noAccess && (
            <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-lg mb-4">
              {refusal}
            </div>
          )}

          {(
            <>
              {/* Google Button */}
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleGoogleLogin}
                loading={googleLoading}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </Button>

              {passwordLogin && <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs text-gray-400">
                  <span className="bg-white px-3">ou</span>
                </div>
              </div>}
            </>
          )}

          {/* Email form — the one-week backup, only while its switch is on. */}
          {passwordLogin && <form onSubmit={handleEmailLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="votre@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" className="w-full" size="lg" loading={loading}>
              Se connecter
            </Button>
          </form>}

          {/* No « Créer un compte »: customer accounts are closed (2026-10-01,
              SignupPage.tsx). This page is for the team. */}
        </div>
      </div>
    </div>
  );
}
